import mixpanel from 'mixpanel-browser';
import React, { Component } from 'react';
import CookieConsent from 'react-cookie-consent';
import 'url-search-params-polyfill';
import '../App.css';
import logo from '../assets/img/vucity-logo.png';

import CloudTips from '../components/CloudTips';
import ErrorMessage from '../components/ErrorMessage';
import FeaturePicker from '../components/FeaturePicker';
import FrameSession from '../components/FrameSession';
import Rating from '../components/Rating';
import Slideshow from '../components/Slideshow/Slideshow';
import SpinnerMessage from '../components/SpinnerMessage';
import TechnicalIssue from '../components/TechnicalError';
import * as consts from '../constants';
import getFrameToken from '../getFrameToken';

import {
  Eighth,
  Fifth,
  First,
  Fourth,
  Second,
  Seventh,
  Sixth,
  Third,
} from '../components/exportImages';
import { Link, Logo, Select } from '../components/UI';

class Hub extends Component {
  constructor() {
    super();

    this.state = {
      mode: consts.modes.spinner,
      status: 'Getting ready...',
      email: '',
      userToken: '',
      loginMethod: '',
      applications: {},
      selection: null,
      backend: null,
      hubRedirectUrl: null,
      frameToken: null,
      featureFlags: [],
      debugKeyDown: false,
    };
  }

  async componentDidMount() {
    // UNCOMMENT THIS FUNCTION CALL IF APP HAS TECHNICAL ISSUES
    // It will show a temporary message to users until problem is fixed
    // Can be used later to determine app status and automatically run it
    // this.isTechnicalIssue();

    // Feture flag picker is hidden behind a held down key,
    // we need to listen for events to detect this
    document.addEventListener('keydown', this.debugKeyEvent);
    document.addEventListener('keyup', this.debugKeyEvent);

    // Grab the URL parameters
    const url = new URL(document.URL);
    const params = new URLSearchParams(url.search);

    // The dashboard will send us this so we can correctly attribute the
    // end-of-session ratings.
    // The email is also our identifier for MixPanel, so get it as early as
    // possible.
    const user = params.get('email');
    if (user) {
      const decodedEmail = decodeURIComponent(user);
      this.setState({ email: decodedEmail });
      mixpanel.identify(decodedEmail);
    } else {
      // Each unknown user counts as a unique user in Mixpanel which costs us
      // extra, so until we get the email log all analytics under the "unknown"
      // user.
      mixpanel.identify('unknown');
    }

    // Read the backend from the parameters
    let backend = params.get('backend');
    if (backend) {
      this.setState({ backend });
    } else {
      backend = 'vu';
    }

    // Request the list of applications from the microservice
    const accountID = (backend === 'vu') ? consts.hubLiveAccountAWS.id : consts.stagingAccountAWS.id;
    await this.loadApplications(accountID, true);

    // Get the auth0 code that we'll parse into the app
    const code = params.get('code');

    // We shouldn't auto login when there are technical issues
    const { mode } = this.state;
    if (mode === consts.modes.blocked) {
      // do nothing
    } else if (code) {
      this.hubLogin(code);
    } else {
      this.setState({
        mode: consts.modes.loginDisallowed,
        userToken: '',
      });
    }
  }

  async componentWillUnmount() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.debugKeyEvent);
    document.removeEventListener('keyup', this.debugKeyEvent);
  }

  loadApplications = async (accountID, updateSelection) => {
    // Time how long this takes
    mixpanel.time_event(consts.eventNames.loadApps);

    const options = {
      method: 'POST',
      headers: {
        Authorization: process.env.REACT_APP_APP_LIST_SERVICE_AUTH,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ account_id: accountID }),
    };

    const response = await fetch(
      process.env.REACT_APP_APP_LIST_SERVICE_URL,
      options,
    );
    if (response.status === 200) {
      const payload = await response.json();

      // Append to the existing hash of applications
      this.setState((prevState) => ({
        applications: { ...prevState.applications, ...payload.applications },
      }));

      // Optionally update the selection state
      if (updateSelection) {
        this.setState({ selection: payload.dashboard_id });
      }
    }

    // Analytics
    mixpanel.track(consts.eventNames.loadApps, {
      success: response.status === 200,
      status: response.status,
      account: consts.accounts[accountID].name,
    });
  };

  hubLogin = (code) => {
    // Remove the auth0 extras from our URL and store it
    const url = new URL(document.URL);
    url.searchParams.delete('code');
    url.searchParams.delete('state');

    this.setState({
      loginMethod: 'auth0',
      userToken: code,
      hubRedirectUrl: url,
    });

    // If the debug key is held, show the feature picker
    const { debugKeyDown } = this.state;
    if (debugKeyDown) {
      this.setMode(consts.modes.featureFlagInput, 'Optional Feature Flags');
    } else {
      this.showSpinner(consts.loggedIn);
      this.retrieveTokenAndStartFrameSession();
    }
  }

  setMode = (mode, status) => {
    this.setState({ mode, status });
  }

  setupUserData = () => {
    const {
      email,
      userToken,
      loginMethod,
      backend,
      hubRedirectUrl,
      featureFlags,
    } = this.state;

    let userData = '{ "error": "Missing Auth Token" }';
    if (userToken) {
      const user = loginMethod === 'auth0' ? hubRedirectUrl : email;

      // App feature flags
      const url = new URL(document.URL);
      const features = url.searchParams.getAll('feature').concat(featureFlags);

      // Used by the hub to control how the app launches
      const project = url.searchParams.get('projectId');
      const file = url.searchParams.get('file');

      const payload = {
        user,
        token: userToken,
        method: loginMethod,
        features,
        backend,
        project,
        file,
      };
      userData = JSON.stringify(payload);
    }
    return userData;
  }

  retrieveTokenAndStartFrameSession = async () => {
    // Time token retrieval
    mixpanel.time_event(consts.eventNames.getToken);

    let frameToken = null;
    let message = '';
    try {
      const { email } = this.state;
      frameToken = await getFrameToken(email);
      // The FrameSession component will handle the rest
      this.setState({ mode: consts.modes.session, frameToken });
    } catch (err) {
      this.showError(`Couldn't authorise cloud session: ${err.message}`);
      message = err.message;
    }

    // Analytics
    mixpanel.track(consts.eventNames.getToken, {
      success: frameToken != null,
      message,
    });
    message = '';
  }

  isTechnicalIssue = () => {
    this.setState({ mode: consts.modes.blocked });
  }

  showSpinner = (message) => {
    this.setState({
      mode: consts.modes.spinner,
      status: message,
    });
  }

  showError = (message) => {
    this.setState({
      mode: consts.modes.error,
      status: message,
    });
  }

  handleFeatures = (featureFlags) => {
    this.setState({ featureFlags });
    this.showSpinner('Launching Cloud Session...');
    this.retrieveTokenAndStartFrameSession();
  }

  handleSelectionChange = (event) => {
    this.setState({ selection: event.target.value });
  };

  debugKeyEvent = (event) => {
    if (event.key === 'Shift') {
      if (event.type === 'keydown') {
        this.setState({ debugKeyDown: true });
      } else if (event.type === 'keyup') {
        this.setState({ debugKeyDown: false });
      }
    }
  }

  render() {
    const {
      status,
      mode,
      email,
      applications,
      selection,
      frameToken,
    } = this.state;

    let launchpadId = '';
    if (applications && selection) {
      launchpadId = consts.accounts[applications[selection].accountID].launchpad;
    }

    // Raise the rating screen above the frame session (to hide stuff we don't want the user to see)
    const index = mode === consts.modes.rating ? 99999999 : 0;

    return (
      <div className="App" style={{ zIndex: index }}>
        <div className="content-area">
          <div className="app-header">
            <Logo url="" imgSrc={logo} />
          </div>

          {
            {
              error: (<ErrorMessage message={status} />),

              spinner: (
                <>
                  <h3 className="app-title">
                    Welcome to VU.CITY Cloud
                  </h3>
                  <SpinnerMessage message={status} />
                </>
              ),

              session: (
                <>
                  <FrameSession
                    authToken={frameToken}
                    launchpadId={launchpadId}
                    appId={selection}
                    userData={this.setupUserData()}
                    setMode={this.setMode}
                  />
                  <CloudTips />
                </>
              ),

              rating: (
                /* TODO: Extract email of login based on ident */
                <Rating
                  email={email || ''}
                  setMode={this.setMode}
                />
              ),

              blocked: (
                <TechnicalIssue>
                  VU.CITY Cloud is currently experiencing technical issues.{' '}
                  <br />
                  We are working to fix these as quickly as we can. <br />
                  Service will resume as soon as possible.
                </TechnicalIssue>
              ),

              loginDisallowed: (
                <TechnicalIssue>
                  It is no longer possible to login via this page.<br />
                  Please return to <Link href="https://hub.vu.city/">the hub</Link> to access VU.CITY Cloud.
                </TechnicalIssue>
              ),

              featureFlagInput: (
                /* HACK: shoving in the version picker on top of the feature input field */
                <div className="app-status">
                  <div>
                    <Select
                      label="VU.CITY Version:"
                      name="select"
                      value={selection}
                      onChangeSelect={this.handleSelectionChange}
                    >
                      {Object.keys(applications).map((key) => (
                        <option key={key} value={key}>
                          {`${applications[key].displayName} ${
                            consts.accounts[applications[key].accountID].tag
                          }`}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <FeaturePicker onComplete={this.handleFeatures} />
                </div>
              ),
            }[mode]
          }
        </div>
        <div className="slide-show">
          <Slideshow slides={[First, Second, Third, Fourth, Fifth, Sixth, Seventh, Eighth]} />
        </div>
        <CookieConsent
          style={{ background: 'var(--petrol-color)', alignItems: 'center' }}
          contentStyle={{ color: 'var(--white-color)' }}
          buttonStyle={{ fontSize: 'var(--large-font)' }}
        >
          In order to continually develop the platform, we gather user data to
          help us make optimal improvements to the platform. We never use your
          information for other purposes. We operate in full compliance with
          GDPR.
        </CookieConsent>
      </div>
    );
  }
}

Hub.propTypes = {};

export default Hub;
